/**
 * Phase 8 · C-44 — Rich `source.*` tool_result card.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.8
 *
 * Replaces the bare summary line for any `tool_result` whose name
 * starts with `source.` and whose structured `data.candidates` payload
 * is present. Each row is a one-liner with name, headline, location
 * and a "Save to pool" CTA.
 *
 * "Save to pool" routes through `candidateService.createFromExternal`
 * which dedupes on `(org_id, external_source, external_id)`. When a
 * match is found we show a "Already in your pool" toast so the same
 * prospect can be clicked twice without polluting the table. A
 * successful save flips the button to "Saved" and links the candidate
 * detail page.
 */
import { useCallback, useMemo, useState } from 'react';
import { BadgeCheck, ExternalLink, Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { useGlobalToast } from '@shared/components/Toast/ToastProvider';
import { candidateService } from '@core/supabase/services/candidateService';
import type { SourcingResult } from '../tools/connectors/types';

export interface AgentSourcingResultsProps {
  toolName: string;
  ok: boolean;
  summary: string;
  data: unknown;
}

interface SavedState {
  status: 'idle' | 'saving' | 'saved' | 'existing' | 'error';
  candidateId?: string | null;
  message?: string;
}

/**
 * Defensive shape check — the runner guarantees `{ candidates: SourcingResult[] }`
 * for `source.*` tools today, but we still unwrap carefully so a future
 * shape change can't crash the UI.
 */
function extractCandidates(raw: unknown): SourcingResult[] {
  if (!raw || typeof raw !== 'object') return [];
  const maybe = (raw as { candidates?: unknown }).candidates;
  if (!Array.isArray(maybe)) return [];
  return maybe.filter(
    (c): c is SourcingResult =>
      c != null &&
      typeof c === 'object' &&
      typeof (c as SourcingResult).externalId === 'string' &&
      typeof (c as SourcingResult).name === 'string' &&
      typeof (c as SourcingResult).source === 'string',
  );
}

function sourceLabel(source: SourcingResult['source']): string {
  switch (source) {
    case 'linkedin':
      return 'LinkedIn';
    case 'naukri':
      return 'Naukri';
    case 'github':
      return 'GitHub';
    case 'gitlab':
      return 'GitLab';
    case 'discord':
      return 'Discord';
    case 'internal':
      return 'Internal pool';
    default:
      return source;
  }
}

/**
 * Colour-coded header pill so a stacked "source + naukri + internal"
 * set of results can be eyeballed at a glance in the thread.
 */
function sourcePillClass(source: SourcingResult['source']): string {
  switch (source) {
    case 'linkedin':
      return 'bg-[#0A66C2]/10 text-[#0A66C2]';
    case 'naukri':
      return 'bg-indigo-500/10 text-indigo-600';
    case 'github':
      return 'bg-neutral-700/10 text-neutral-700';
    case 'gitlab':
      return 'bg-orange-500/10 text-orange-600';
    case 'discord':
      return 'bg-[#5865F2]/10 text-[#5865F2]';
    case 'internal':
      return 'bg-emerald-500/10 text-emerald-600';
    default:
      return 'bg-neutral-500/10 text-neutral-500';
  }
}

export function AgentSourcingResults({
  toolName,
  ok,
  summary,
  data,
}: AgentSourcingResultsProps) {
  const { user } = useAuth();
  const { showToast } = useGlobalToast();
  const orgId = user?.orgId ?? '';
  const orgUserId = user?.orgUserId ?? null;

  const candidates = useMemo(() => extractCandidates(data), [data]);
  const [saveState, setSaveState] = useState<Record<string, SavedState>>({});

  const saveOne = useCallback(
    async (c: SourcingResult) => {
      if (!orgId) {
        showToast({ title: 'Sign in to your org to save candidates.', variant: 'error' });
        return;
      }
      const key = `${c.source}:${c.externalId}`;
      setSaveState((prev) => ({ ...prev, [key]: { status: 'saving' } }));
      try {
        const { action, candidate } = await candidateService.createFromExternal({
          orgId,
          externalSource: c.source,
          externalId: c.externalId,
          name: c.name,
          headline: c.headline ?? null,
          profileUrl: c.profileUrl,
          locationText: c.locationText ?? null,
          avatarUrl: c.avatarUrl ?? null,
          createdByOrgUserId: orgUserId,
        });
        if (action === 'existing') {
          setSaveState((prev) => ({
            ...prev,
            [key]: { status: 'existing', candidateId: candidate.id },
          }));
          showToast({
            title: 'Already in your pool',
            body: `${c.name} is already saved — opened the existing candidate.`,
            variant: 'info',
          });
        } else {
          setSaveState((prev) => ({
            ...prev,
            [key]: { status: 'saved', candidateId: candidate.id },
          }));
          showToast({
            title: 'Saved to pool',
            body: c.name,
            variant: 'success',
          });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Save failed';
        setSaveState((prev) => ({ ...prev, [key]: { status: 'error', message: msg } }));
        showToast({ title: 'Could not save', body: msg, variant: 'error' });
      }
    },
    [orgId, orgUserId, showToast],
  );

  // Error / empty short-circuits — keep the existing muted card shape.
  if (!ok) {
    return (
      <div
        className="rounded-[8px] border px-2.5 py-1.5 text-[12px]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'var(--vscode-bg-hover)',
          color: '#9F1239',
        }}
      >
        <div className="mb-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider">
          {toolName}
        </div>
        <div className="whitespace-pre-wrap">{summary}</div>
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div
        className="rounded-[8px] border px-2.5 py-1.5 text-[12px]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'var(--vscode-bg-hover)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        <div className="mb-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
          {toolName}
        </div>
        <div className="whitespace-pre-wrap">{summary}</div>
      </div>
    );
  }

  const source = candidates[0].source;
  return (
    <div
      className="rounded-[10px] border"
      style={{
        borderColor: 'var(--vscode-border-primary)',
        background: 'var(--vscode-bg-hover)',
      }}
    >
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b" style={{ borderColor: 'var(--vscode-border-primary)' }}>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${sourcePillClass(source)}`}
        >
          {sourceLabel(source)}
        </span>
        <span className="text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
          {candidates.length} result{candidates.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="flex flex-col divide-y" style={{ borderColor: 'var(--vscode-border-primary)' }}>
        {candidates.slice(0, 8).map((c) => {
          const key = `${c.source}:${c.externalId}`;
          const state = saveState[key] ?? { status: 'idle' };
          const saved = state.status === 'saved' || state.status === 'existing';
          return (
            <li key={key} className="flex items-start gap-2 px-2.5 py-2">
              {c.avatarUrl ? (
                <img
                  src={c.avatarUrl}
                  alt=""
                  className="h-7 w-7 rounded-full object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="h-7 w-7 rounded-full bg-neutral-300 flex-shrink-0" aria-hidden />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <a
                    href={c.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[12px] font-medium truncate hover:underline"
                    style={{ color: 'var(--vscode-text-primary)' }}
                  >
                    {c.name}
                  </a>
                  <ExternalLink className="h-3 w-3 opacity-60 flex-shrink-0" aria-hidden />
                </div>
                {c.headline ? (
                  <div className="text-[11px] truncate" style={{ color: 'var(--vscode-text-secondary)' }}>
                    {c.headline}
                  </div>
                ) : null}
                {c.locationText ? (
                  <div className="text-[10px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                    {c.locationText}
                  </div>
                ) : null}
              </div>
              <div className="flex-shrink-0">
                {saved && state.candidateId ? (
                  <a
                    href={`/candidates/${state.candidateId}`}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium"
                    style={{
                      borderColor: 'var(--vscode-border-primary)',
                      color: state.status === 'existing' ? '#9A6B00' : '#065F46',
                      background: 'transparent',
                    }}
                  >
                    <BadgeCheck className="h-3 w-3" aria-hidden />
                    {state.status === 'existing' ? 'In pool' : 'Saved'}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => void saveOne(c)}
                    disabled={state.status === 'saving'}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium disabled:opacity-60"
                    style={{
                      borderColor: 'var(--vscode-border-primary)',
                      color: 'var(--vscode-text-primary)',
                      background: 'transparent',
                    }}
                  >
                    {state.status === 'saving' ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    ) : (
                      <UserPlus className="h-3 w-3" aria-hidden />
                    )}
                    {state.status === 'saving' ? 'Saving…' : 'Save to pool'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {candidates.length > 8 ? (
        <div
          className="px-2.5 py-1 text-[10px]"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          + {candidates.length - 8} more not shown
        </div>
      ) : null}
    </div>
  );
}
